import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Settings, DollarSign, TrendingUp, FileText, Shield,
  Users, AlertCircle, CheckCircle, Clock, XCircle,
  Percent, Save, RefreshCw, ChevronRight, Building2,
  BarChart3, PieChart, AlertTriangle, Package, Eye,
  Search, Filter, ChevronDown, Truck
} from 'lucide-react';
import ShipStationAccountsPage from './ShipStationAccountsPage';
import SelleTransClaimReview from '@/components/SelleTransClaimReview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlatformAdminPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const [overview, setOverview] = useState(null);
  const [clients, setClients] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [allClaims, setAllClaims] = useState(null);
  const [insuranceReports, setInsuranceReports] = useState(null);
  const [claimFilter, setClaimFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    checkAdminRole();
  }, [token]);

  useEffect(() => {
    if (isAdmin) {
      if (activeTab === 'overview') fetchOverview();
      if (activeTab === 'clients') fetchClients();
      if (activeTab === 'claims') fetchAllClaims();
      if (activeTab === 'reports') fetchInsuranceReports();
    }
  }, [activeTab, token, isAdmin]);

  const checkAdminRole = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/role`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIsAdmin(data.is_platform_admin);
        if (data.is_platform_admin) {
          fetchOverview();
        }
      }
    } catch (err) {
      console.error('Failed to check admin role:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverview = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/platform/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOverview(data);
      }
    } catch (err) {
      console.error('Failed to fetch overview:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/platform/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data);
      }
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  };

  const fetchClientDetail = async (clientId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/platform/client/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedClient(data);
      }
    } catch (err) {
      console.error('Failed to fetch client detail:', err);
    }
  };

  const fetchAllClaims = async (filtro = claimFilter) => {
    try {
      let url = `${API_URL}/api/admin/platform/insurance/claims`;
      if (filtro) url += `?status=${filtro}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAllClaims(data);
      }
    } catch (err) {
      console.error('Failed to fetch claims:', err);
    }
  };

  const fetchInsuranceReports = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/platform/insurance/reports`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInsuranceReports(data);
      }
    } catch (err) {
      console.error('Failed to fetch insurance reports:', err);
    }
  };

  const updateClaimStatus = async (claimId, status, payoutAmount = null) => {
    try {
      const body = { status };
      if (payoutAmount !== null) body.payout_amount = payoutAmount;
      
      const res = await fetch(`${API_URL}/api/admin/platform/insurance/claim/${claimId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (res.ok) {
        fetchAllClaims();
        alert('Claim status updated!');
      }
    } catch (err) {
      console.error('Failed to update claim:', err);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'clients', label: 'Clients', icon: Users },
    { id: 'claims', label: 'Insurance Claims', icon: Shield },
    { id: 'reports', label: 'Insurance Reports', icon: PieChart },
    { id: 'shipstation', label: 'ShipStation', icon: Truck }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'under_review': return 'bg-blue-100 text-blue-700';
      case 'approved': return 'bg-green-100 text-green-700';
      case 'paid': return 'bg-emerald-100 text-emerald-700';
      case 'denied': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <Shield className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-xl font-bold mb-2">Access Denied</h2>
        <p className="text-slate-500">You do not have platform admin privileges.</p>
        <p className="text-sm text-slate-400 mt-2">Contact support if you believe this is an error.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">Platform Admin</h1>
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">Admin</span>
          </div>
          <p className="text-slate-500">Manage platform settings, clients, and reports</p>
        </div>
        <Button variant="outline" onClick={() => activeTab === 'overview' && fetchOverview()} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800 pb-2 overflow-x-auto">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            onClick={() => setActiveTab(tab.id)}
            className="whitespace-nowrap"
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
        
        {/* Overview Tab */}
        {activeTab === 'overview' && overview && (
          <div className="space-y-6" data-testid="overview-section">
            <h2 className="text-lg font-semibold">Platform Overview - {overview.period}</h2>
            
            {/* Main Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                <Users className="w-8 h-8 mb-2 opacity-80" />
                <p className="text-3xl font-bold">{overview.users.total}</p>
                <p className="text-sm opacity-80">Total Users</p>
                <p className="text-xs mt-1">+{overview.users.new_this_month} this month</p>
              </div>
              
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
                <Package className="w-8 h-8 mb-2 opacity-80" />
                <p className="text-3xl font-bold">{overview.shipments.total}</p>
                <p className="text-sm opacity-80">Total Shipments</p>
                <p className="text-xs mt-1">+{overview.shipments.this_month} this month</p>
              </div>
              
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
                <Shield className="w-8 h-8 mb-2 opacity-80" />
                <p className="text-3xl font-bold">{overview.fraud_screenings.total}</p>
                <p className="text-sm opacity-80">Fraud Screenings</p>
                <p className="text-xs mt-1">+{overview.fraud_screenings.this_month} this month</p>
              </div>
              
              {/* Era un "Month Revenue" inventado: cada revision de fraude a 4,99, cobrada o no,
                  mas las primas declaradas. Lo cobrado de verdad esta en Revenue (14-oct). */}
              <Link to="/dashboard/admin/revenue" className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white hover:ring-2 hover:ring-emerald-300" data-testid="platform-revenue-link">
                <DollarSign className="w-8 h-8 mb-2 opacity-80" />
                <p className="text-lg font-bold">Revenue</p>
                <p className="text-sm opacity-80">What was actually charged, by source</p>
              </Link>
            </div>

            {/* Secondary Stats */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <h3 className="font-medium mb-3">Insurance</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Declarations</span>
                    <span className="font-bold">{overview.insurance.total_declarations}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Total Claims</span>
                    <span className="font-bold">{overview.insurance.total_claims}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-yellow-600">Pending Claims</span>
                    <span className="font-bold text-yellow-600">{overview.insurance.pending_claims}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
                <h3 className="font-medium mb-3">Subscriptions</h3>
                <p className="text-3xl font-bold text-emerald-600">{overview.subscriptions.active}</p>
                <p className="text-sm text-slate-500">Active Subscriptions</p>
              </div>

            </div>
          </div>
        )}

        {/* Clients Tab */}
        {activeTab === 'clients' && (
          <div className="space-y-6" data-testid="clients-section">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">All Clients</h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search clients..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
                <Button variant="outline" onClick={fetchClients}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {selectedClient ? (
              <div className="space-y-4">
                <Button variant="ghost" onClick={() => setSelectedClient(null)}>
                  ← Back to Clients
                </Button>
                
                <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6">
                  <h3 className="text-xl font-bold">{selectedClient.client.company_name}</h3>
                  <p className="text-slate-500">{selectedClient.client.email}</p>
                  <p className="text-xs text-slate-400">Joined: {new Date(selectedClient.client.created_at).toLocaleDateString()}</p>
                  
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="bg-white dark:bg-slate-900 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold">{selectedClient.stats.total_shipments}</p>
                      <p className="text-xs text-slate-500">Shipments</p>
                    </div>
                    <div className="bg-white dark:bg-slate-900 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold">{selectedClient.stats.total_screenings}</p>
                      <p className="text-xs text-slate-500">Screenings</p>
                    </div>
                    <div className="bg-white dark:bg-slate-900 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold">{selectedClient.stats.total_claims}</p>
                      <p className="text-xs text-slate-500">Claims</p>
                    </div>
                  </div>
                </div>

                {selectedClient.subscription && (
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4">
                    <h4 className="font-medium">Active Subscription</h4>
                    <p className="text-lg font-bold">{selectedClient.subscription.plan_name}</p>
                    <p className="text-sm text-slate-500">${selectedClient.subscription.price}/month</p>
                  </div>
                )}

                {/* Mostraba una tabla vieja de margenes que no fijaba lo que se cobra (14-oct). */}
                <Link to="/dashboard/admin/merchants" className="block bg-slate-50 dark:bg-slate-800 rounded-xl p-4 hover:ring-1 hover:ring-emerald-400" data-testid="client-setup-link">
                  <h4 className="font-medium">Margin, coverage and limits</h4>
                  <p className="text-sm text-slate-500">Set per merchant in Admin, Merchants.</p>
                </Link>
              </div>
            ) : clients ? (
              <div className="space-y-2">
                {clients.clients
                  .filter(c => 
                    !searchQuery || 
                    c.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    c.email?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((client, i) => (
                  <div 
                    key={i} 
                    className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                    onClick={() => fetchClientDetail(client.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium">{client.company_name}</p>
                        <p className="text-sm text-slate-500">{client.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right text-sm">
                        <p>{client.stats.shipments} shipments</p>
                        <p className="text-slate-500">{client.stats.screenings} screenings</p>
                      </div>
                      {client.subscription_plan && (
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded">
                          {client.subscription_plan}
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                ))}
                <p className="text-sm text-slate-500 text-center pt-4">
                  Showing {clients.clients.length} of {clients.total} clients
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            )}
          </div>
        )}

        {/* Insurance Claims Tab */}
        {activeTab === 'claims' && (
          <div className="space-y-6" data-testid="claims-section">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">All Insurance Claims</h2>
              <div className="flex items-center gap-2">
                <select
                  value={claimFilter}
                  onChange={(e) => {
                    // The new value goes in directly: reading the state right
                    // after setting it gave the previous filter.
                    setClaimFilter(e.target.value);
                    fetchAllClaims(e.target.value);
                  }}
                  className="border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">All Status</option>
                  <option value="submitted">Submitted</option>
                  <option value="pending">Pending</option>
                  <option value="under_review">Under Review</option>
                  <option value="needs_info">Needs Info</option>
                  <option value="approved">Approved</option>
                  <option value="paid">Paid</option>
                  <option value="denied">Denied</option>
                </select>
                <Button variant="outline" onClick={() => fetchAllClaims()}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {allClaims ? (
              <div className="space-y-3">
                {allClaims.claims.length > 0 ? allClaims.claims.map((claim, i) => claim.claim_number ? (
                  <SelleTransClaimReview key={claim.claim_number} claim={claim} token={token} onDone={() => fetchAllClaims()} />
                ) : (
                  <div key={i} className="border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(claim.status)}`}>
                            {claim.status}
                          </span>
                          <span className="font-medium capitalize">{claim.claim_type} Claim</span>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">{claim.description}</p>
                        {claim.user_info && (
                          <p className="text-xs text-slate-500 mt-1">
                            Client: {claim.user_info.company_name} ({claim.user_info.email})
                          </p>
                        )}
                        <p className="text-xs text-slate-400 mt-1">
                          Filed: {new Date(claim.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-lg font-bold">${claim.claim_amount}</p>
                        <p className="text-xs text-slate-500">Coverage: ${claim.coverage_amount}</p>
                        {claim.payout_amount && (
                          <p className="text-sm text-emerald-600">Paid: ${claim.payout_amount}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    {claim.status === 'pending' && (
                      <div className="flex gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => updateClaimStatus(claim.id, 'under_review')}
                        >
                          Start Review
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => updateClaimStatus(claim.id, 'approved', claim.claim_amount)}
                        >
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => updateClaimStatus(claim.id, 'denied')}
                        >
                          Deny
                        </Button>
                      </div>
                    )}
                    {claim.status === 'approved' && (
                      <div className="flex gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <Button 
                          size="sm" 
                          className="bg-emerald-500 hover:bg-emerald-600"
                          onClick={() => updateClaimStatus(claim.id, 'paid', claim.claim_amount)}
                        >
                          Mark as Paid
                        </Button>
                      </div>
                    )}
                  </div>
                )) : (
                  <div className="text-center py-12 text-slate-500">
                    <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No claims found</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            )}
          </div>
        )}

        {/* Insurance Reports Tab */}
        {activeTab === 'reports' && insuranceReports && (
          <div className="space-y-6" data-testid="reports-section">
            <h2 className="text-lg font-semibold">Insurance Reports</h2>

            {/* Summary */}
            <div className="grid md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                <h3 className="text-sm opacity-80">Total Premiums</h3>
                <p className="text-2xl font-bold">${insuranceReports.summary.total_premiums}</p>
                <p className="text-xs opacity-80">{insuranceReports.summary.total_declarations} declarations</p>
              </div>
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
                <h3 className="text-sm opacity-80">Total Claims</h3>
                <p className="text-2xl font-bold">{insuranceReports.summary.total_claims}</p>
              </div>
              <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white">
                <h3 className="text-sm opacity-80">Total Payouts</h3>
                <p className="text-2xl font-bold">${insuranceReports.summary.total_payouts}</p>
              </div>
              <div className={`bg-gradient-to-br ${insuranceReports.summary.loss_ratio < 50 ? 'from-emerald-500 to-emerald-600' : 'from-orange-500 to-orange-600'} rounded-xl p-4 text-white`}>
                <h3 className="text-sm opacity-80">Loss Ratio</h3>
                <p className="text-2xl font-bold">{insuranceReports.summary.loss_ratio}%</p>
                <p className="text-xs opacity-80">Profit: ${insuranceReports.summary.profit_margin}</p>
              </div>
            </div>

            {/* Claims by Status */}
            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6">
              <h3 className="font-medium mb-4">Claims by Status</h3>
              <div className="grid grid-cols-5 gap-4">
                {Object.entries(insuranceReports.claims_by_status).map(([status, count]) => (
                  <div key={status} className="text-center">
                    <p className="text-2xl font-bold">{count}</p>
                    <p className={`text-xs capitalize ${getStatusColor(status)} px-2 py-0.5 rounded inline-block mt-1`}>
                      {status}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Monthly Trends */}
            <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-6">
              <h3 className="font-medium mb-4">Monthly Trends</h3>
              <div className="space-y-3">
                {insuranceReports.monthly_trends.map((month, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 rounded-lg">
                    <span className="font-medium">{month.month}</span>
                    <div className="flex items-center gap-6 text-sm">
                      <span>{month.declarations} declarations</span>
                      <span className="text-emerald-600">${month.premiums} premiums</span>
                      <span>{month.claims} claims</span>
                      <span className="text-red-500">${month.payouts} payouts</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ShipStation Tab */}
        {activeTab === 'shipstation' && (
          <div data-testid="shipstation-tab">
            <ShipStationAccountsPage />
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformAdminPage;
